import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, DialogTitle } from '@mui/material'
import React, { useState } from 'react'
import { useUser } from 'src/Hooks/useUser'
import { ModbusTCPServerDto, ModbusTCPServerLicenseType } from '../../types/modbus-tcp-server.types'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { ShellSelectProps } from '@e3dc-react/shell/Elements/Select/FeatureRichSelect'
import { useDialogActions } from '../../../components/Dialogs/hooks'

//#region Dialog
export interface ActivateModbusTcpServerDialogProps {
  latestData: Nullable<ModbusTCPServerDto> | null
  active: boolean
  onSave: (licenceType: ModbusTCPServerLicenseType) => void
  onClose?: () => void
}

/** Dialog to activate the Modbus TCP server */
export const ActivateModbusTcpServerDialog: React.FC<ActivateModbusTcpServerDialogProps> = ({ latestData, active, onSave, onClose }) => {
  const { t } = useCustomTranslation()

  const { userData: user } = useUser()

  const [type, setType] = useState<ModbusTCPServerLicenseType>('disabled')

  const isSuperUser = user?.level === 'EXT_SERVICE_PLUS'
  const canActivateFullVersion = isSuperUser
  const canContinueTrial =
    (latestData?.type === 'free_trial' || latestData?.type === 'free_trial_continuation') && !!latestData?.interruptionDate && isSuperUser

  const selectOptions: ShellSelectProps<ModbusTCPServerLicenseType>['options'] = [
    {
      label: t('installation.modbusTCPServer.options.free-trial'),
      value: 'free_trial',
    },
    {
      label: t('installation.modbusTCPServer.options.unlimited'),
      value: 'unlimited',
      isDisabled: !canActivateFullVersion,
    },
    {
      label: t('installation.modbusTCPServer.options.free-trial-continue'),
      value: 'free_trial_continuation',
      isDisabled: active || !canContinueTrial,
    },
  ]

  const handleSave = (): void => {
    onClose?.()
    onSave(type)
  }
  const handleOnClose = (): void => {
    onClose?.()
  }

  const dialogActions = useDialogActions({
    primary: {
      name: t('installation.modbusTCPServer.activation.confirm'),
      onClick: handleSave,
      disabled: type === 'disabled',
    },
    secondary: { onClick: handleOnClose },
  })

  return (
    <Dialog maxWidth="md" onClose={handleOnClose} title={t('installation.modbusTCPServer.activation.title')} actions={dialogActions}>
      <DialogTitle variant="h2">{t('installation.modbusTCPServer.question')}</DialogTitle>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, py: 2, px: 3 }}>
        <Select value={type} onChange={setType} options={selectOptions} />
      </Box>
    </Dialog>
  )
}
//#endregion
